import { Link } from "@tanstack/react-router";
import { ArrowUpRight, FileSearch } from "lucide-react";
import { useTop5InputsQuery } from "@/lib/portal-queries";
import { usePortalRuntime } from "@/lib/portal-runtime";
import { WorkspacePage } from "./PortalAppShell";
import { LiveDashboard } from "./LiveDashboard";
import { ClaimsVolumeTrend } from "./ClaimsVolumeTrend";
import { HistoricalFailureModes } from "./HistoricalFailureModes";
import { PortalDataError, PortalLoadingState } from "./PortalDataState";

export function WarrantyDashboard() {
  const { tenantContext } = usePortalRuntime();
  return (
    <WorkspacePage
      title="Executive Warranty Dashboard"
      subtitle={`${tenantContext.tenantName} · source-backed warranty intelligence`}
    >
      <LiveDashboard embedded showPreviewNotice={false} />
      <section className="grid gap-5 xl:grid-cols-3">
        <ManagementTopFive />
        <section className="rounded-lg border border-border bg-card panel-glow">
          <div className="border-b border-border px-4 py-3">
            <h2 className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              From claim to action
            </h2>
            <p className="mt-1 text-sm">A guide to this workspace</p>
          </div>
          <ol className="space-y-4 p-4 text-sm">
            <li>
              <strong className="text-primary">1. Understand the history.</strong>
              <p className="mt-1 text-muted-foreground">
                Claims Volume shows when repairs were recorded. Failure Modes ranks descriptions
                from the imported claims.
              </p>
            </li>
            <li>
              <strong className="text-primary">2. Review the patterns.</strong>
              <p className="mt-1 text-muted-foreground">
                Top 5 links the highest-ranked source patterns to their evidence and Problem
                Records.
              </p>
            </li>
            <li>
              <strong className="text-primary">3. Open the case.</strong>
              <p className="mt-1 text-muted-foreground">
                A historical draft is a review candidate. It does not establish root cause,
                severity, or an approved corrective action.
              </p>
            </li>
          </ol>
          <div className="flex flex-wrap gap-2 border-t border-border p-4">
            <Link
              to="/problem-records"
              className="rounded bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground"
            >
              Open Problem Records
            </Link>
            <Link to="/data-confidence" className="rounded border border-border px-3 py-2 text-xs">
              Check data confidence
            </Link>
          </div>
        </section>
      </section>
      <div className="grid gap-5 xl:grid-cols-3">
        <ClaimsVolumeTrend />
        <HistoricalFailureModes />
      </div>
      <p className="rounded-lg border border-border bg-card px-4 py-3 text-xs leading-5 text-muted-foreground">
        Read the source, cutoff date, and quality indicators on each panel. Historical imports
        describe past repairs; they do not describe current operations. Unsupported forecasts and
        rates remain unavailable.
      </p>
    </WorkspacePage>
  );
}

function ManagementTopFive() {
  const query = useTop5InputsQuery();
  const widget = query.data?.widgets.find((item) => item.payload.category === "problems");
  return (
    <section className="rounded-lg border border-border bg-card panel-glow xl:col-span-2">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
        <div>
          <h2 className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            Management Top 5
          </h2>
          <p className="mt-1 text-sm">Source-ranked patterns linked to review records</p>
        </div>
        <Link to="/top5" className="inline-flex items-center gap-1 text-xs text-primary">
          Top 5 intelligence
          <ArrowUpRight className="size-3.5" aria-hidden="true" />
        </Link>
      </div>
      {query.isPending ? (
        <div className="p-4">
          <PortalLoadingState label="Loading ranked warranty patterns" />
        </div>
      ) : query.isError ? (
        <div className="p-4">
          <PortalDataError error={query.error} onRetry={() => void query.refetch()} />
        </div>
      ) : !widget || widget.dataState === "UNAVAILABLE" || widget.payload.items.length === 0 ? (
        <div className="p-6 text-sm text-muted-foreground">
          <FileSearch className="mb-2 size-5" aria-hidden="true" />
          {widget?.payload.unavailableReason ?? "No governed problem ranking is available."}
        </div>
      ) : (
        <>
          <ol className="divide-y divide-border px-4">
            {widget.payload.items.slice(0, 5).map((issue) => (
              <li key={issue.id} className="flex items-start gap-3 py-3">
                <span className="num pt-1 text-lg text-muted-foreground">{issue.rank}</span>
                <div className="min-w-0 flex-1">
                  {issue.problemRecord ? (
                    <Link
                      to="/problem-record/$id"
                      params={{ id: issue.problemRecord.id }}
                      className="block text-sm font-medium hover:text-primary"
                    >
                      {issue.title}
                    </Link>
                  ) : (
                    <p className="text-sm font-medium">{issue.title}</p>
                  )}
                  <p className="mt-1 text-xs text-muted-foreground">
                    {issue.rankingBasis.label} ·{" "}
                    {issue.claimCount === undefined
                      ? "Claim count unavailable"
                      : `${issue.claimCount.toLocaleString()} linked claims`}
                  </p>
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    {issue.dataState === "SYNTHETIC" ||
                    issue.authorityClass === "DEMONSTRATION_ONLY"
                      ? "Synthetic demonstration data"
                      : issue.freshness === "STALE"
                        ? "Historical real data"
                        : "Governed source data"}{" "}
                    · {issue.quality} · {issue.reconciliation} · Data through{" "}
                    {new Date(issue.dataCutoffAt).toLocaleDateString()}
                  </p>
                </div>
                <span className="shrink-0 rounded border border-border px-2 py-1 text-[11px] text-muted-foreground">
                  {issue.signal.severity ?? "Unassessed"}
                </span>
              </li>
            ))}
          </ol>
          <p className="border-t border-border px-4 py-3 text-[11px] leading-5 text-muted-foreground">
            {widget.sourceSummary} · As of {new Date(widget.asOf).toLocaleDateString()}.
          </p>
        </>
      )}
    </section>
  );
}
